/**
 * OpenView — открытый текстовый ответ (kind="open").
 *
 * Игрок вводит текст в `<textarea>` (ограничение `kindData.maxLen`, если задано),
 * нажимает «Ответить» → onAnswer({kind:'open', data:{text}}). После отправки ввод
 * блокируется. На reveal показывается список допустимых ответов (accept) и
 * предварительная отметка, совпал ли ответ игрока (клиентская нормализация;
 * истина скоринга — на сервере). Spectate (ведущий, Фаза 10 И3): read-only
 * предпросмотр — disabled-поле как у игрока (без кнопки «Ответить»), после
 * reveal — список accept.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isOpenReveal, openMaxLen, type PlayerAnswer } from '../../protocol';
import { normalizeOpenText, type QuestionBodyProps } from './types';
import { FormulaText } from '../FormulaText';

export function OpenView({
  question,
  variant,
  myAnswer,
  reveal,
  onAnswer,
}: QuestionBodyProps): JSX.Element {
  const { t } = useTranslation();
  const maxLen = openMaxLen(question);
  const initialText = myAnswer?.kind === 'open' ? myAnswer.data.text : '';
  const [text, setText] = useState(initialText);
  const [submitted, setSubmitted] = useState(myAnswer?.kind === 'open');

  const locked = variant === 'answer' && (submitted || reveal !== null);
  const revealData = reveal?.reveal && isOpenReveal(reveal.reveal) ? reveal.reveal : null;

  function handleSubmit(): void {
    if (locked) return;
    const value = text.trim();
    if (!value) return;
    setSubmitted(true);
    const answer: PlayerAnswer = { kind: 'open', data: { text: value } };
    onAnswer(answer);
  }

  const matched =
    revealData && submitted
      ? revealData.accept.some((a) => normalizeOpenText(a) === normalizeOpenText(text.trim()))
      : null;

  // reveal-box общий для игрока и spectate-предпросмотра (И3, Баг-О): у ведущего
  // нет своего ответа → matched === null, отметка не рендерится.
  const revealBox = revealData ? (
    <div className="reveal-box" role="status">
      <p className="reveal-box__title">
        {t('open.acceptLabel')}{' '}
        <strong>
          {revealData.accept.length > 0 ? <FormulaText text={revealData.accept.join(', ')} /> : '—'}
        </strong>
      </p>
      {matched !== null ? (
        <p className={matched ? 'q-hint q-hint--ok' : 'q-hint q-hint--wrong'}>
          {matched ? t('open.correct') : t('open.wrong')}
        </p>
      ) : null}
    </div>
  ) : null;

  if (variant === 'spectate') {
    // Баг-О (Фаза 10, И3): предпросмотр поля ввода для ведущего — то же поле,
    // что у игрока, но disabled и без кнопки отправки; после reveal — accept.
    return (
      <div className="open-view">
        <textarea
          className="textarea open-view__input"
          value=""
          placeholder={t('open.placeholder')}
          maxLength={maxLen}
          disabled
          readOnly
          aria-label={t('open.aria')}
          data-testid="open-answer-input"
        />
        {revealBox}
      </div>
    );
  }

  return (
    <div className="open-view">
      <textarea
        className="textarea open-view__input"
        value={text}
        placeholder={t('open.placeholder')}
        maxLength={maxLen}
        disabled={locked}
        onChange={(e) => setText(e.target.value)}
        aria-label={t('open.aria')}
        data-testid="open-answer-input"
      />
      <div className="open-view__row">
        {typeof maxLen === 'number' ? (
          <span className="q-hint small">
            {text.length}/{maxLen}
          </span>
        ) : null}
        {submitted && !revealData ? (
          <span className="q-hint q-hint--ok">{t('open.answered')}</span>
        ) : null}
        <button
          type="button"
          className="cta open-view__submit"
          disabled={locked || text.trim().length === 0}
          onClick={handleSubmit}
        >
          {t('common.answer')}
        </button>
      </div>

      {revealBox}
    </div>
  );
}
